import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { AdminLayout } from '../components/layouts/AdminLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, FileText, Calculator, BookOpen, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminResourcesPage = () => {
    const { language } = useLanguage();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        title: '', title_en: '', description: '', description_en: '',
        category: 'pdf_notes', file_url: '', file_type: 'pdf'
    });

    const categories = [
        { value: 'pdf_notes', label: 'PDF Notes', icon: FileText },
        { value: 'trading_plan', label: 'Trading Plan', icon: BookOpen },
        { value: 'risk_calculator', label: 'Risk Calculator', icon: Calculator },
        { value: 'trading_journal', label: 'Trading Journal', icon: BookOpen }
    ];

    const fileTypes = ['pdf', 'xlsx', 'docx', 'zip'];

    // Ensure auth token is set on component mount
    useEffect(() => {
        const token = localStorage.getItem('nusatrade_token');
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
    }, []);

    useEffect(() => {
        fetchResources();
    }, []);

    const fetchResources = async () => {
        try {
            const response = await axios.get(`${API}/resources`);
            setResources(response.data);
        } catch (error) {
            console.error('Error fetching resources:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('nusatrade_token');
            if (!token) {
                toast.error(language === 'id' ? 'Sila login semula' : 'Please login again');
                return;
            }
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            
            if (editing) {
                await axios.put(`${API}/admin/resources/${editing.id}`, form, config);
                toast.success(language === 'id' ? 'Resource diupdate!' : 'Resource updated!');
            } else {
                await axios.post(`${API}/admin/resources`, form, config);
                toast.success(language === 'id' ? 'Resource ditambahkan!' : 'Resource added!');
            }
            setDialogOpen(false);
            setEditing(null);
            resetForm();
            fetchResources();
        } catch (error) {
            console.error('Save resource error:', error);
            toast.error(language === 'id' ? 'Gagal simpan resource' : 'Error saving resource');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm(language === 'id' ? 'Yakin hapus resource ini?' : 'Delete this resource?')) return;
        try {
            const token = localStorage.getItem('nusatrade_token');
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            await axios.delete(`${API}/admin/resources/${id}`, config);
            toast.success(language === 'id' ? 'Resource dihapus!' : 'Resource deleted!');
            fetchResources();
        } catch (error) {
            toast.error('Error deleting resource');
        }
    };

    const resetForm = () => {
        setForm({
            title: '', title_en: '', description: '', description_en: '',
            category: 'pdf_notes', file_url: '', file_type: 'pdf'
        });
    };

    const openEdit = (resource) => {
        setEditing(resource);
        setForm({
            title: resource.title || '',
            title_en: resource.title_en || '',
            description: resource.description || '',
            description_en: resource.description_en || '',
            category: resource.category || 'pdf_notes',
            file_url: resource.file_url || '',
            file_type: resource.file_type || 'pdf'
        });
        setDialogOpen(true);
    };

    const getCategoryIcon = (cat) => {
        const found = categories.find(c => c.value === cat);
        return found ? found.icon : FileText;
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32" />)}
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-heading font-bold text-white">
                            {language === 'id' ? 'Kelola Resources' : 'Manage Resources'}
                        </h1>
                        <p className="text-slate-400 mt-1">{resources.length} resources</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={() => { setEditing(null); resetForm(); }} data-testid="add-resource-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                {language === 'id' ? 'Tambah Resource' : 'Add Resource'}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
                            <DialogHeader>
                                <DialogTitle>
                                    {editing ? (language === 'id' ? 'Edit Resource' : 'Edit Resource') : (language === 'id' ? 'Tambah Resource' : 'Add Resource')}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Title (ID)</Label>
                                        <Input
                                            value={form.title}
                                            onChange={(e) => setForm({...form, title: e.target.value})}
                                            className="bg-slate-700 border-slate-600"
                                            data-testid="resource-title"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Title (EN)</Label>
                                        <Input
                                            value={form.title_en}
                                            onChange={(e) => setForm({...form, title_en: e.target.value})}
                                            className="bg-slate-700 border-slate-600"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Description (ID)</Label>
                                    <Textarea
                                        value={form.description}
                                        onChange={(e) => setForm({...form, description: e.target.value})}
                                        className="bg-slate-700 border-slate-600"
                                        data-testid="resource-description"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Description (EN)</Label>
                                    <Textarea
                                        value={form.description_en}
                                        onChange={(e) => setForm({...form, description_en: e.target.value})}
                                        className="bg-slate-700 border-slate-600"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Category</Label>
                                        <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                                            <SelectTrigger className="bg-slate-700 border-slate-600">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-700 border-slate-600">
                                                {categories.map(c => (
                                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>File Type</Label>
                                        <Select value={form.file_type} onValueChange={(v) => setForm({...form, file_type: v})}>
                                            <SelectTrigger className="bg-slate-700 border-slate-600">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-700 border-slate-600">
                                                {fileTypes.map(ft => (
                                                    <SelectItem key={ft} value={ft}>{ft.toUpperCase()}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>File URL</Label>
                                    <Input
                                        value={form.file_url}
                                        onChange={(e) => setForm({...form, file_url: e.target.value})}
                                        className="bg-slate-700 border-slate-600"
                                        placeholder="https://..."
                                        data-testid="resource-url"
                                    />
                                </div>
                                <div className="flex justify-end gap-2 pt-4">
                                    <Button variant="outline" onClick={() => setDialogOpen(false)} className="bg-slate-700 border-slate-600">
                                        {language === 'id' ? 'Batal' : 'Cancel'}
                                    </Button>
                                    <Button onClick={handleSave} data-testid="save-resource-btn">
                                        {language === 'id' ? 'Simpan' : 'Save'}
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Resources List */}
                <div className="grid gap-4">
                    {resources.length === 0 ? (
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="py-12 text-center">
                                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                                <p className="text-slate-400">{language === 'id' ? 'Belum ada resource' : 'No resources yet'}</p>
                            </CardContent>
                        </Card>
                    ) : (
                        resources.map((resource) => {
                            const Icon = getCategoryIcon(resource.category);
                            return (
                                <Card key={resource.id} className="bg-slate-800 border-slate-700" data-testid={`resource-${resource.id}`}>
                                    <CardContent className="py-4">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex items-start gap-4 flex-1">
                                                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                                                    <Icon className="w-6 h-6 text-red-400" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h3 className="font-semibold text-white truncate">{resource.title}</h3>
                                                        <span className="px-2 py-0.5 rounded text-xs bg-slate-600 text-slate-300">
                                                            {resource.file_type?.toUpperCase() || 'PDF'}
                                                        </span>
                                                    </div>
                                                    <p className="text-slate-400 text-sm line-clamp-2">{resource.description}</p>
                                                    <div className="flex items-center gap-4 mt-2">
                                                        <p className="text-slate-500 text-xs">
                                                            {categories.find(c => c.value === resource.category)?.label || resource.category}
                                                        </p>
                                                        <p className="text-slate-500 text-xs flex items-center gap-1">
                                                            <Download className="w-3 h-3" />
                                                            {resource.download_count || 0} downloads
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => openEdit(resource)}
                                                    className="text-slate-400 hover:text-white"
                                                    data-testid={`edit-resource-${resource.id}`}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => handleDelete(resource.id)}
                                                    className="text-slate-400 hover:text-red-400"
                                                    data-testid={`delete-resource-${resource.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};
